import { create } from 'zustand';
import api from '@/lib/api';
import type {
 ApiResponse,
 StartOnboardingResponse,
 GetProgressResponse,
 SaveStepProgressResponse,
 CompleteOnboardingResponse,
 UserProfile,
 Skill,
 ParsedResumeData,
} from '@/types/onboarding';

interface OnboardingState {
 currentStep: number;
 totalSteps: number;
 isLoading: boolean;
 error: string | null;
 sessionId: string | null;
 resumeData: ParsedResumeData | null;
 userProfile: Partial<UserProfile> | null;
 completedSteps: number[];

 // Actions
 startOnboarding: () => Promise<void>;
 setStep: (step: number) => void;
 nextStep: () => void;
 previousStep: () => void;
 setResumeData: (data: ParsedResumeData) => void;
 updateUserProfile: (data: Partial<UserProfile>) => void;
 initialize: () => Promise<GetProgressResponse | null>;
 completeOnboarding: () => Promise<void>;
 saveStepProgress: (step: number, data: any) => Promise<void>;
 parseResumeText: (text: string) => Promise<ParsedResumeData>;
 getSkills: (filters?: any) => Promise<Skill[]>;
 getUserProfile: () => Promise<UserProfile>;
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
 currentStep: 1,
 totalSteps: 3, // Updated to match ImprovedOnboardingPage steps
 isLoading: false,
 error: null,
 sessionId: null,
 resumeData: null,
 userProfile: null,
 completedSteps: [],

 startOnboarding: async () => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<ApiResponse<StartOnboardingResponse>>(
    '/onboarding/start'
   );
   const { session } = response.data.data;
   set({
    sessionId: session.id,
    currentStep: session.currentStep || 1,
    completedSteps: session.completedSteps || [],
    isLoading: false,
   });
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to start onboarding',
    isLoading: false,
   });
  }
 },

 setStep: (step: number) => set({ currentStep: step }),

 nextStep: () => {
  const { currentStep, totalSteps } = get();
  if (currentStep < totalSteps) set({ currentStep: currentStep + 1 });
 },

 previousStep: () => {
  const { currentStep } = get();
  if (currentStep > 1) set({ currentStep: currentStep - 1 });
 },

 setResumeData: (data) => set({ resumeData: data }),

 updateUserProfile: (data) => {
  const { userProfile } = get();
  set({ userProfile: { ...userProfile, ...data } });
 },

 initialize: async () => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.get<ApiResponse<GetProgressResponse>>(
    '/onboarding/progress'
   );
   const { session } = response.data.data;
   if (session) {
    set({
     sessionId: session.id,
     currentStep: session.currentStep || 1,
     completedSteps: session.completedSteps || [],
    });
   } else {
    await get().startOnboarding();
   }
   set({ isLoading: false });
   return response.data.data;
  } catch (error) {
   await get().startOnboarding();
   set({ isLoading: false });
   return null;
  }
 },

 saveStepProgress: async (step, data) => {
  let { sessionId } = get();

  // Wait for sessionId if it's not ready (up to 5 seconds)
  if (!sessionId) {
   console.log('[Onboarding Store] No sessionId, waiting...');
   for (let i = 0; i < 10; i++) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    sessionId = get().sessionId;
    if (sessionId) break;
   }
  }

  if (!sessionId) {
   console.error(
    '[Onboarding Store] Failed to save progress: No session ID after wait'
   );
   throw new Error('Onboarding session not initialized');
  }

  try {
   await api.post<ApiResponse<SaveStepProgressResponse>>(
    `/onboarding/step/${sessionId}/${step}`,
    data
   );
   const completedSteps = [...new Set([...get().completedSteps, step])];
   set({ completedSteps });
  } catch (error: any) {
   throw new Error(
    error.response?.data?.error?.message || 'Failed to save progress'
   );
  }
 },

 completeOnboarding: async () => {
  const { sessionId } = get();
  if (!sessionId) return;
  set({ isLoading: true, error: null });
  try {
   await api.post<ApiResponse<CompleteOnboardingResponse>>(
    `/onboarding/complete/${sessionId}`
   );
   set({ isLoading: false });
  } catch (error: any) {
   set({
    error:
     error.response?.data?.error?.message || 'Failed to complete onboarding',
    isLoading: false,
   });
   throw error;
  }
 },

 parseResumeText: async (text) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<ApiResponse<ParsedResumeData>>(
    '/onboarding/parse-resume-text',
    { text }
   );
   set({ resumeData: response.data.data, isLoading: false });
   return response.data.data;
  } catch (error: any) {
   const message =
    error.response?.data?.error?.message || 'Failed to parse resume';
   set({ error: message, isLoading: false });
   throw new Error(message);
  }
 },

 getSkills: async (filters) => {
  try {
   const response = await api.get<ApiResponse<{ skills: Skill[] }>>(
    '/onboarding/skills',
    { params: filters }
   );
   return response.data.data.skills;
  } catch (error: any) {
   throw new Error(
    error.response?.data?.error?.message || 'Failed to fetch skills'
   );
  }
 },

 getUserProfile: async () => {
  try {
   const response = await api.get<ApiResponse<{ profile: UserProfile }>>(
    '/onboarding/profile'
   );
   const profile = response.data.data.profile;
   set({ userProfile: profile });
   return profile;
  } catch (error: any) {
   throw new Error(
    error.response?.data?.error?.message || 'Failed to get profile'
   );
  }
 },
}));
